import { useEffect, useMemo, useRef, useState } from "react";
import { getNextTableName } from "../../../../../api/bills";

/** Позволените знаци в името на маса: букви, цифри, интервал, точка, тире. */
const keepTableNameCharacters = (text: string) =>
  text.replace(/[^\p{L}\p{N} .-]/gu, "");

interface TableNameState {
  value: string;
  /** Стойността е още предложеният номер — първият въведен знак го заменя. */
  suggested: boolean;
}

const EMPTY_NAME: TableNameState = { value: "", suggested: false };

/**
 * Какво остава от полето, когато човек пише ВЪРХУ предложения номер през
 * самия input (телефонната клавиатура). Браузърът казва какво е станало
 * (`InputEvent.inputType`): изтриване маха предложението цялото, въведен знак
 * го заменя. Без InputEvent (тестове, автопопълване) — махаме предложението
 * от началото/края на написаното, иначе приемаме написаното както е.
 */
export const typedOverSuggestion = (
  typed: string,
  suggestion: string,
  inputEvent?: Event,
): string => {
  const inputType = (inputEvent as InputEvent | undefined)?.inputType;
  if (inputType?.startsWith("delete")) return "";
  const insertedText = (inputEvent as InputEvent | undefined)?.data;
  if (inputType === "insertText" && insertedText) return insertedText;
  if (typed.length > suggestion.length) {
    if (typed.startsWith(suggestion)) return typed.slice(suggestion.length);
    if (typed.endsWith(suggestion)) return typed.slice(0, -suggestion.length);
  }
  return typed;
};

interface NewTableNameInputOptions {
  open: boolean;
  /** Настройката на ресторанта „Предлагане на номер за нова маса"
   *  (`orderingSuggestTableName`). */
  suggestName: boolean;
}

/**
 * Полето с името в „Нова маса". При `suggestName` всяко отваряне иска
 * първия свободен номер от същия източник като бързото отваряне —
 * `GET /bills/next-table-name` (BE-то брои и чуждите маси при обща номерация;
 * в локален режим отговаря касата). Номерът е ПРЕДЛОЖЕНИЕ: първата цифра/
 * буква го заменя, Backspace го трие целия, ОК го приема както е. Резервация
 * на номера се пита при ОК от обичайната проверка на модала
 * (`newTableNameCheck.ts`) — тук не се повтаря.
 *
 * Провалена заявка (стар BE, спрял интернет) → полето остава празно, без
 * съобщение: предложението е удобство. Написаното преди отговора не се
 * презаписва.
 */
export const useNewTableNameInput = ({
  open,
  suggestName,
}: NewTableNameInputOptions) => {
  const [name, setName] = useState<TableNameState>(EMPTY_NAME);
  // Човекът вече е пипнал полето в това отваряне — късно пристигнало
  // предложение не презаписва написаното от него.
  const touchedRef = useRef(false);

  useEffect(() => {
    if (!open || !suggestName) return;
    touchedRef.current = false;
    let cancelled = false;
    getNextTableName()
      .then(({ name: suggestion }) => {
        if (cancelled || touchedRef.current || !suggestion) return;
        setName((current) =>
          current.value === "" ? { value: suggestion, suggested: true } : current,
        );
      })
      .catch(() => {
        // Без предложение модалът е празен, както без настройката.
      });
    return () => {
      cancelled = true;
    };
  }, [open, suggestName]);

  // Стабилни функции — глобалният keydown на модала ги държи в ефект.
  const actions = useMemo(() => {
    const edit = (next: (current: TableNameState) => string) => {
      touchedRef.current = true;
      setName((current) => ({
        value: keepTableNameCharacters(next(current)),
        suggested: false,
      }));
    };
    return {
      typeCharacter: (character: string) =>
        edit((current) =>
          current.suggested ? character : current.value + character,
        ),
      deleteLast: () =>
        edit((current) => (current.suggested ? "" : current.value.slice(0, -1))),
      clear: () => edit(() => ""),
      changeFromInput: (typed: string, inputEvent?: Event) =>
        edit((current) =>
          current.suggested
            ? typedOverSuggestion(typed, current.value, inputEvent)
            : typed,
        ),
      reset: () => {
        touchedRef.current = false;
        setName(EMPTY_NAME);
      },
    };
  }, []);

  return { tableName: name.value, isSuggested: name.suggested, ...actions };
};
